import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref } from "vue";
import {
  AdvancedExportGenerateFilterInfoDTO,
  AdvancedExportGenerateIterationFilterInfoDTO,
  AdvancedExportGenerateRequestDTO,
  AdvancedExportIntegrationAvailabilityDTO,
  AdvancedExportOutputType,
  ErrorTypesAdvancedExportJobArgumentsAdvancedExportJobResultAdvancedExportJobResultLongPollingJobInfoDTO,
  LongPollingJobStatus,
  Orientation,
  PageType,
} from "../common/api/autogenerated";
import TableauPopup from "../../ui-components/TableauPopup.vue";
import TableauButton from "../../ui-components/TableauButton.vue";
import TableauCheckBox from "../../ui-components/TableauCheckBox.vue";
import TableauLink from "../../ui-components/TableauLink.vue";
import TableauSelect from "../../ui-components/select/TableauSelect.vue";
import TableauRadioButton from "../../ui-components/TableauRadioButton.vue";
import { container } from "tsyringe";
import { AdvancedExportModule, CategoricalFilterInfo } from "./advanced-export.module";
import { CommonModule } from "../common.module";
import { UiModule } from "../common/ui.module";
import FilterSelectorPopup from "./FilterSelectorPopup.vue";
import IterationFilterSelectorPopup from "./IterationFilterSelectorPopup.vue";
import { ApiModule } from "../common/api/api.module";

interface State {
  exportType: "simple" | "advanced";
  selectedViewIds: string[];
  filters: { name: string; value: string }[];
  iterationFilter?: { name: string; values: string[] };
  outputType: AdvancedExportOutputType[];
  pageOrientation: Orientation[];
  pageSize: PageType[];
  mergePdfForViews: boolean;
  mergePdfForIterationFilter: boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'AdvancedExportPopup',
  props: {
    data: { type: Object, required: true },
    screenshot: { type: null, required: true }
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const props = __props;

const module = container.resolve(AdvancedExportModule);
const uiModule = container.resolve(UiModule);
const common = container.resolve(CommonModule);
const apiModule = container.resolve(ApiModule);


const status = ref<"idle" | "loading" | "success" | "error">("idle");

const canExport = computed(() => {
  if (state.exportType === "simple") {
    return true;
  }
  if (state.selectedViewIds.length === 0) {
    return false;
  }
  return true;
});
const errorMessage = ref<string>("");
const submitMessage = ref<string>("");

const statusMessage = computed(() => {
  switch (status.value) {
    case "idle":
      if (state.exportType === "simple") {
        return "One image will be generated";
      }
      if (state.selectedViewIds.length === 0) {
        return "Please select at least one view to export";
      }
      const viewsCount = state.selectedViewIds.length;
      const ifsCount = state.iterationFilter?.values?.length || 1;
      let generateCount = viewsCount * ifsCount;
      if (state.outputType[0] === "pdf") {
        if (state.mergePdfForIterationFilter && state.mergePdfForViews) {
          generateCount = 1;
        } else if (state.mergePdfForIterationFilter) {
          generateCount = viewsCount;
        } else if (state.mergePdfForViews) {
          generateCount = ifsCount;
        }
      }
      const type = state.outputType[0] === "pdf" ? "PDF Document" : "PNG Image";
      return `${generateCount === 1 ? "One " : `${generateCount} `} ${type}${
        generateCount > 1 ? "s" : ""
      } will be generated`;
    case "loading":
      return isCancelling.value
        ? "Cancelling..."
        : submitMessage.value || `Generating output, please wait...`;
    case "error":
      return errorMessage.value || "Failed to generate output";
    case "success":
      return "";
  }
});
const currentSubmitJobId = ref<string>("");
const isCancelling = ref<boolean>(false);
const cancelLinkInfo = computed(() => {
  if (status.value !== "loading" || !currentSubmitJobId.value) {
    return undefined;
  }
  return { text: "Cancel", action: () => cancelCurrentJob() };
});
const advancedControlsDisabled = computed(
  () => status.value === "loading" || state.exportType === "simple"
);

let state: State = reactive({
  exportType: props.screenshot ? "simple" : "advanced",
  selectedViewIds: [props.data.currentViewId?.toString()],
  filters: [],
  iterationFilter: undefined,
  outputType: [AdvancedExportOutputType.Pdf],
  pageOrientation: [Orientation.Landscape],
  pageSize: [PageType.Legal],
  mergePdfForIterationFilter: false,
  mergePdfForViews: true,
});
async function getOutputTypes(): Promise<{ id: string; text: string }[]> {
  return [
    { id: AdvancedExportOutputType.Image, text: "PNG Image" },
    { id: AdvancedExportOutputType.Pdf, text: "PDF Document" },
  ];
}
async function getPageOrientations(): Promise<{ id: string; text: string }[]> {
  return [
    { id: Orientation.Portrait, text: "Portrait" },
    { id: Orientation.Landscape, text: "Landscape" },
  ];
}
async function getPageSizes(): Promise<{ id: string; text: string }[]> {
  return [
    { id: PageType.A3, text: "A3" },
    { id: PageType.A4, text: "A4" },
    { id: PageType.A5, text: "A5" },
    { id: PageType.B5, text: "B5" },
    { id: PageType.Executive, text: "Executive" },
    { id: PageType.Folio, text: "Folio" },
    { id: PageType.Ledger, text: "Ledger" },
    { id: PageType.Legal, text: "Legal" },
    { id: PageType.Letter, text: "Letter" },
    { id: PageType.Note, text: "Note" },
    { id: PageType.Quarto, text: "Quarto" },
    { id: PageType.Tabloid, text: "Tabloid" },
  ];
}
function getLocalStateKey() {
  return `stm-ae-${window.location.pathname}`;
}

let getCategoricalFilterInfos: Promise<CategoricalFilterInfo[]> = undefined;

onMounted(() => {
  // start to get Categorical Filter Infos
  getCategoricalFilterInfos = module.getSingleCategoricalFilterNames().catch((e) => {
    module.log.e("Failed to load Tableau Filter info", e);
    return [];
  });
  // try to get state from local storage
  try {
    const localState = window.localStorage.getItem(getLocalStateKey());
    if (localState) {
      const localStateObj = JSON.parse(localState);
      Object.keys(localStateObj).forEach((k) => (state[k] = localStateObj[k]));
    }
  } catch (e) {
    module.log.e("Failed to retrieve Advanced Export state from LocalStorage");
  }
});
async function getViewList(): Promise<{ id: string; text: string }[]> {
  return props.data.views.map((e) => ({ id: e.id?.toString(), text: e.name }));
}

const addingCurrentFilters = ref<boolean>(false);
async function addCurrentFilters() {
  if (addingCurrentFilters.value) {
    return;
  }
  addingCurrentFilters.value = true;
  const f = await getCategoricalFilterInfos;
  const filters = f
    .filter((e) => !!e.singleAppliedValue)
    .map((e) => ({
      name: e.name,
      value: e.singleAppliedValue,
    }));
  filters.forEach((fi) => {
    const existingIndex = state.filters.findIndex((e) => e.name === fi.name);
    if (existingIndex > -1) {
      state.filters.splice(existingIndex, 1, fi);
    } else {
      state.filters.push(fi);
    }
  });
  addingCurrentFilters.value = false;
}

function editFilter(filter?: { name: string; value: string }) {
  const container = document.createElement("div");

  let editIndex = -1;
  if (filter) {
    editIndex = state.filters.findIndex((e) => e.name === filter.name);
  }
  const existingFilters = [...state.filters];
  if (state.iterationFilter) {
    existingFilters.push({
      name: state.iterationFilter.name,
      value: state.iterationFilter.values[0],
    });
  }
  const popup = uiModule.injectUI(FilterSelectorPopup, container, {
    existingFilter: filter,
    allExistingFilters: existingFilters,
    allPossibleFilterNames: getCategoricalFilterInfos.then((e) => e.map((f) => f.name)),
    onSubmit: (value?: { name: string; value: string }) => {
      if (value) {
        if (editIndex > -1) {
          state.filters.splice(editIndex, 1, value);
        } else {
          state.filters.push(value);
        }
      }
      popup.unmount();
      container.remove();
    },
  });
  document.body.appendChild(container);
}
function deleteFilter(filter?: { name: string; value: string }) {
  const i = state.filters.findIndex((e) => e.name === filter.name);
  if (i > -1) {
    state.filters.splice(i, 1);
  }
}

function manageIterationFilter() {
  const container = document.createElement("div");
  const popup = uiModule.injectUI(IterationFilterSelectorPopup, container, {
    initialValue: state.iterationFilter,
    allExistingFilters: state.filters,
    allPossibleFilterNames: getCategoricalFilterInfos.then((e) => e.map((f) => f.name)),
    onSubmit: (value?: {
      cmd: "add" | "remove";
      filter: { name: string; values: string[] };
    }) => {
      switch (value?.cmd) {
        case "add":
          state.iterationFilter = value.filter;
          break;
        case "remove":
          state.iterationFilter = undefined;
          break;
      }
      popup.unmount();
      container.remove();
    },
  });
  document.body.appendChild(container);
}

async function doExport() {
  //save state to localstorage
  window.localStorage.setItem(getLocalStateKey(), JSON.stringify(state));
  status.value = "loading";
  errorMessage.value = "";
  submitMessage.value = "";
  currentSubmitJobId.value = "";
  isCancelling.value = false;
  let dataPromise: Promise<{ dataUrl: string; fileName: string }>;
  switch (state.exportType) {
    case "simple":
      dataPromise = Promise.resolve({
        dataUrl: props.screenshot,
        fileName: "Dashboard_image",
      });
      break;
    case "advanced":
      dataPromise = apiModule
        .apiRequest((e) =>
          e.apiAdvancedExportIntegrationGenerateStart(
            new AdvancedExportGenerateRequestDTO({
              outputType: state.outputType[0],
              pdfMergeIterationFilters: state.mergePdfForIterationFilter,
              pdfMergeViews: state.mergePdfForViews,
              pdfOrientation: state.pageOrientation[0],
              pdfSize: state.pageSize[0],
              serverId: window.stm.config.serverId,
              viewIds: state.selectedViewIds.map((e) => +e),
              workbookId: props.data.currentWorkbookId,
              filters: state.filters.map(
                (e) =>
                  new AdvancedExportGenerateFilterInfoDTO({
                    name: e.name,
                    value: e.value,
                  })
              ),
              iterationFilter: state.iterationFilter
                ? new AdvancedExportGenerateIterationFilterInfoDTO({
                    name: state.iterationFilter.name,
                    values: state.iterationFilter.values,
                  })
                : undefined,
            })
          )
        )
        .then((initialApi) => {
          if (!initialApi.success) {
            module.log.e("Failed to start generation job", initialApi.error);
            errorMessage.value = "Failed to start generation job";
            throw new Error("Failed to start generation job");
          }
          currentSubmitJobId.value = initialApi.value.id;
          return getStatusAndDownloadFile(initialApi.value);
        });

      break;
  }

  try {
    const ret = await dataPromise;
    common.downloadFile(ret.dataUrl, ret.fileName);
    status.value = "idle";
  } catch (e) {
    module.log.e("Failed to export", e);
    status.value = "error";
  }
}

async function getStatusAndDownloadFile(
  resp: ErrorTypesAdvancedExportJobArgumentsAdvancedExportJobResultAdvancedExportJobResultLongPollingJobInfoDTO
): Promise<{ dataUrl: string; fileName: string }> {
  submitMessage.value = resp.progress?.progressMessage;
  if (resp.status !== LongPollingJobStatus.Running) {
    currentSubmitJobId.value = "";
  }
  switch (resp.status) {
    case LongPollingJobStatus.Canceled:
      errorMessage.value = "Job has been canceled";
      throw new Error();
    case LongPollingJobStatus.TimedOut:
      errorMessage.value = "Job has timed out";
      throw new Error();
    case LongPollingJobStatus.Error:
      errorMessage.value = resp.error?.message || "Job has failed";
      throw new Error();
    case LongPollingJobStatus.Success:
      submitMessage.value = "Output generated, downloading...";
      const api = await apiModule.blobDataUrlRequest((e) =>
        e.apiAdvancedExportIntegrationGenerateDownload(resp.id)
      );
      if (!api.success) {
        errorMessage.value = "Failed to download file";
        throw new Error();
      }
      return {
        dataUrl: api.value,
        fileName: resp.result.fileName || "Unknown",
      };
    case LongPollingJobStatus.Running:
      const status = await apiModule.apiRequest((e) =>
        e.apiAdvancedExportIntegrationGenerateStatus(resp.id)
      );
      if (!status.success) {
        module.log.e("Failed to request job status", status.error);
        errorMessage.value = "Failed to request job status";
        throw new Error("Failed to request job status");
      }
      await common.wait(1000);
      return await getStatusAndDownloadFile(status.value);
      break;
  }
}

async function cancelCurrentJob() {
  if (!currentSubmitJobId.value) {
    return;
  }
  isCancelling.value = true;
  const api = await apiModule.apiRequest((e) =>
    e.apiAdvancedExportIntegrationGenerateCancel(currentSubmitJobId.value)
  );
  if (!api.success) {
    isCancelling.value = false;
    module.log.e("Failed to cancel", api.error);
  }
}

const __returned__ = { module, uiModule, common, apiModule, props, status, canExport, errorMessage, submitMessage, statusMessage, currentSubmitJobId, isCancelling, cancelLinkInfo, advancedControlsDisabled, get state() { return state }, set state(v) { state = v }, getOutputTypes, getPageOrientations, getPageSizes, getLocalStateKey, get getCategoricalFilterInfos() { return getCategoricalFilterInfos }, set getCategoricalFilterInfos(v) { getCategoricalFilterInfos = v }, getViewList, addingCurrentFilters, addCurrentFilters, editFilter, deleteFilter, manageIterationFilter, doExport, getStatusAndDownloadFile, cancelCurrentJob, TableauPopup, TableauButton, TableauCheckBox, TableauLink, TableauSelect, TableauRadioButton }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})